import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { authenticatedFetch } from '@/lib/api';
import { PRIVILEGE, TAB_VISIBILITY_PRIVILEGES } from '@/lib/privilegeConstants';

export interface UserPrivilegeItem {
  Id: number;
  PrevilageId: number;
  PrevilageName: string;
}

export interface UserPrivilegesData {
  UserId: string;
  UserName: string;
  RoleId: number;
  RoleName: string;
  Privileges: UserPrivilegeItem[];
}

interface GetUserPrivilegesResponse {
  status?: number;
  message?: string;
  data?: Array<{ UserPrivilegesData: UserPrivilegesData }>;
}

interface UserPrivilegesContextValue {
  /** Set of privilege IDs assigned to the current user (empty if not loaded or not logged in). */
  privilegeIds: Set<number>;
  /** Raw user/role info from last successful fetch. */
  userPrivilegesData: UserPrivilegesData | null;
  /** Whether the last fetch is in progress. */
  loading: boolean;
  /** Error message if the last fetch failed. */
  error: string | null;
  /** Fetch user privileges (call after login or token validation). */
  fetchUserPrivileges: () => Promise<void>;
  /** Check if the user has a given privilege by ID. */
  hasPrivilege: (previlageId: number) => boolean;
  /** Check if the user has any of the given privilege IDs. */
  hasAnyPrivilege: (previlageIds: number[]) => boolean;
  /** Clear privileges (e.g. on logout). */
  clearPrivileges: () => void;
}

const UserPrivilegesContext = createContext<UserPrivilegesContextValue | null>(null);

export function UserPrivilegesProvider({ children }: { children: React.ReactNode }) {
  const [privilegeIds, setPrivilegeIds] = useState<Set<number>>(new Set());
  const [userPrivilegesData, setUserPrivilegesData] = useState<UserPrivilegesData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUserPrivileges = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch<GetUserPrivilegesResponse>('/api/GetUserPrivileges');
      const raw = res?.data as any;

      // Console: full API response (for debugging)
      console.log('[GetUserPrivileges] API response:', JSON.stringify(raw, null, 2));

      // API can return { data: [...], status, message } – array is at raw.data
      const list = Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw)
          ? raw
          : [];
      const first = list[0]?.UserPrivilegesData;

      console.log('[GetUserPrivileges] Parsed list length:', list.length, 'first item:', first ? { UserId: first.UserId, UserName: first.UserName, RoleName: first.RoleName, PrivilegesCount: first.Privileges?.length } : null);

      if (!first?.Privileges || !Array.isArray(first.Privileges)) {
        console.warn('[GetUserPrivileges] No privileges array found – showing all tabs as fallback. Raw first:', first);
        setPrivilegeIds(new Set());
        setUserPrivilegesData(null);
        setLoading(false);
        return;
      }

      const ids = new Set<number>(first.Privileges.map((p) => p.PrevilageId));
      setPrivilegeIds(ids);
      setUserPrivilegesData(first);

      // Console: mapping – which privilege IDs we have and how they map to tabs/cards
      const idList = Array.from(ids).sort((a, b) => a - b);
      console.log('[GetUserPrivileges] Mapped privilege IDs:', idList);
      console.log('[GetUserPrivileges] Tab visibility mapping:', {
        transactions: TAB_VISIBILITY_PRIVILEGES.transactions?.some((id) => ids.has(id)),
        reports: TAB_VISIBILITY_PRIVILEGES.reports?.some((id) => ids.has(id)),
        administrator: TAB_VISIBILITY_PRIVILEGES.administrator?.some((id) => ids.has(id)),
      });
      console.log('[GetUserPrivileges] User:', first.UserName, 'Role:', first.RoleName, 'Privilege count:', ids.size);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load user privileges';
      console.error('[GetUserPrivileges] Error:', err);
      console.error('[GetUserPrivileges] Error message:', message);
      setError(message);
      setPrivilegeIds(new Set());
      setUserPrivilegesData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const hasPrivilege = useCallback(
    (previlageId: number) => privilegeIds.has(previlageId),
    [privilegeIds]
  );

  const hasAnyPrivilege = useCallback(
    (previlageIdsList: number[]) => previlageIdsList.some((id) => privilegeIds.has(id)),
    [privilegeIds]
  );

  const clearPrivileges = useCallback(() => {
    setPrivilegeIds(new Set());
    setUserPrivilegesData(null);
    setError(null);
  }, []);

  const value = useMemo<UserPrivilegesContextValue>(
    () => ({
      privilegeIds,
      userPrivilegesData,
      loading,
      error,
      fetchUserPrivileges,
      hasPrivilege,
      hasAnyPrivilege,
      clearPrivileges,
    }),
    [
      privilegeIds,
      userPrivilegesData,
      loading,
      error,
      fetchUserPrivileges,
      hasPrivilege,
      hasAnyPrivilege,
      clearPrivileges,
    ]
  );

  return (
    <UserPrivilegesContext.Provider value={value}>
      {children}
    </UserPrivilegesContext.Provider>
  );
}

export function useUserPrivileges(): UserPrivilegesContextValue {
  const ctx = useContext(UserPrivilegesContext);
  if (!ctx) {
    throw new Error('useUserPrivileges must be used within UserPrivilegesProvider');
  }
  return ctx;
}

/** Safe hook: returns null if used outside provider; otherwise same as useUserPrivileges(). */
export function useUserPrivilegesOptional(): UserPrivilegesContextValue | null {
  return useContext(UserPrivilegesContext);
}
